import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import PWAInstallBanner from "./components/PWAInstallBanner";
import IOSInstallBanner from "./components/IOSInstallBanner";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import BranchesPage from "./pages/BranchesPage";
import BrandsPage from "./pages/BrandsPage";
import TravelAllowancePage from "./pages/TravelAllowancePage";
import BrandPayslipsPage from "./pages/BrandPayslipsPage";
import AttendancePage from "./pages/AttendancePage";
import LeavePage from "./pages/LeavePage";
import LeaveDetailsPage from "./pages/LeaveDetailsPage";
import TasksPage from "./pages/TasksPage";
import AchievementsPage from "./pages/AchievementsPage";
import TestsPage from "./pages/TestsPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import EmployeeProfilePage from "./pages/EmployeeProfilePage";
import AuditLogPage from "./pages/AuditLogPage";
import CompliancePage from "./pages/CompliancePage";
import MeetingPhotosPage from "./pages/MeetingPhotosPage";
import KonattuTargetsPage from "./pages/KonattuTargetsPage";
import MonthlySummaryPage from "./pages/MonthlySummaryPage";
import RequestsPage from "./pages/RequestsPage";
import DataBankPage from "./pages/DataBankPage";
import RecruitmentPage from "./pages/RecruitmentPage";
import PhotoMonitoringPage from "./pages/PhotoMonitoringPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import PayrollPage from "./pages/PayrollPage";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-neutral-500">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.system_role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <PWAInstallBanner />
      <IOSInstallBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="employees" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><EmployeesPage /></ProtectedRoute>} />
            <Route path="branches" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><BranchesPage /></ProtectedRoute>} />
            <Route path="brands" element={<ProtectedRoute allowedRoles={['super_admin']}><BrandsPage /></ProtectedRoute>} />
            <Route path="travel-allowance" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><TravelAllowancePage /></ProtectedRoute>} />
            <Route path="brand-payslips" element={<ProtectedRoute><BrandPayslipsPage /></ProtectedRoute>} />
            <Route path="data-bank" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><DataBankPage /></ProtectedRoute>} />
            <Route path="recruitment" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><RecruitmentPage /></ProtectedRoute>} />
            <Route path="photo-monitoring" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><PhotoMonitoringPage /></ProtectedRoute>} />
            <Route path="analytics" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><AnalyticsPage /></ProtectedRoute>} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="leave" element={<LeavePage />} />
            <Route path="leave-details" element={<LeaveDetailsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="achievements" element={<AchievementsPage />} />
            <Route path="tests" element={<TestsPage />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="audit-log" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><AuditLogPage /></ProtectedRoute>} />
            <Route path="compliance" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><CompliancePage /></ProtectedRoute>} />
            <Route path="meeting-photos" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><MeetingPhotosPage /></ProtectedRoute>} />
            <Route path="konattu-achievements" element={<KonattuTargetsPage />} />
            <Route path="monthly-summary" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><MonthlySummaryPage /></ProtectedRoute>} />
            <Route path="employees/:employeeId/profile" element={<ProtectedRoute allowedRoles={['super_admin', 'management', 'branch_manager']}><EmployeeProfilePage /></ProtectedRoute>} />
            <Route path="complaints" element={<ComplaintsPage />} />
            <Route path="payroll" element={<ProtectedRoute allowedRoles={['super_admin', 'management']}><PayrollPage /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
